/**
 * LazyBillingProvider — a BillingProvider whose real implementation is
 * chosen asynchronously, on first use.
 *
 * WHY: `resolveBilling()` is called SYNCHRONOUSLY inside the zustand store
 * factory (useStore.ts, `create(...)`), but choosing the live provider needs
 * two async facts — is this a native shell (`native/native.ts`, loaded
 * dynamically so Capacitor never enters the web bundle) and can the
 * RevenueCat plugin be loaded (`revenueCatPlugin.ts`, the ONE file that
 * imports the package). Every BillingProvider method is already a Promise,
 * so deferring the choice costs nothing at the call sites.
 *
 * A failed load is NOT cached: the next call retries, so a transient
 * plugin-load failure on a cold start does not brick billing for the
 * session.
 */

import type { BillingProvider, PaidPlan, Subscription } from '../types';
import type { StorePrices } from './prices';

/**
 * Optional hooks a provider may implement so store entitlements follow the
 * ACCOUNT (Firebase uid), not the install. The mock does not; RevenueCat
 * does. Callers use `isAccountAware()` rather than assuming.
 */
export interface AccountAwareBilling {
  /** Bind the store customer to this uid; returns the entitlement it now holds, or null when unavailable. */
  identify(uid: string): Promise<Subscription | null>;
  /** Detach on sign-out. Must never throw. */
  forget(): Promise<void>;
  /** Re-read the current entitlement (boot, foreground). Null when unavailable. */
  refresh(): Promise<Subscription | null>;
}

export function isAccountAware(p: BillingProvider): p is BillingProvider & AccountAwareBilling {
  const c = p as Partial<AccountAwareBilling>;
  return (
    typeof c.identify === 'function' &&
    typeof c.forget === 'function' &&
    typeof c.refresh === 'function'
  );
}

export class LazyBillingProvider implements BillingProvider, AccountAwareBilling {
  readonly name: string;
  private inner: Promise<BillingProvider> | null = null;

  constructor(
    private readonly load: () => Promise<BillingProvider>,
    name = 'Store billing',
  ) {
    this.name = name;
  }

  private resolve(): Promise<BillingProvider> {
    if (!this.inner) {
      this.inner = this.load().catch((err) => {
        this.inner = null;
        throw err;
      });
    }
    return this.inner;
  }

  async subscribe(plan: PaidPlan): Promise<Subscription> {
    return (await this.resolve()).subscribe(plan);
  }
  async cancel(): Promise<Subscription> {
    return (await this.resolve()).cancel();
  }
  async restore(): Promise<Subscription | null> {
    return (await this.resolve()).restore();
  }
  async purchaseScanPack(): Promise<number> {
    return (await this.resolve()).purchaseScanPack();
  }
  /** Diagnostics: forwards to the inner provider's selfTest when it has one. */
  async selfTest(): Promise<string[]> {
    const inner = (await this.resolve()) as BillingProvider & { selfTest?: () => Promise<string[]> };
    if (typeof inner.selfTest !== 'function') return [`${inner.name}: no self-test on this provider`];
    return inner.selfTest();
  }

  /** Store price labels for the paywall; {} when the inner provider has none (mock/web). */
  async storePrices(): Promise<StorePrices> {
    const inner = (await this.resolve()) as BillingProvider & {
      storePrices?: () => Promise<StorePrices>;
    };
    return typeof inner.storePrices === 'function' ? inner.storePrices() : {};
  }

  async identify(uid: string): Promise<Subscription | null> {
    const p = await this.resolve();
    return isAccountAware(p) ? p.identify(uid) : null;
  }
  async forget(): Promise<void> {
    const p = await this.resolve();
    if (isAccountAware(p)) await p.forget();
  }
  async refresh(): Promise<Subscription | null> {
    const p = await this.resolve();
    return isAccountAware(p) ? p.refresh() : null;
  }
}
